/**
 * English dictionary, the source of truth for shape and content. Every
 * other locale's dictionary must have exactly the same key paths (see
 * dictionaries.test.ts) with translated leaf strings.
 */
export const en = {
  common: {
    skipToMainContent: 'Skip to main content',
  },
  meta: {
    homeTitle: 'Datacode | AI Integration, LLM Solutions & Web Development Agency',
    homeDescription:
      'Datacode is a digital agency in Erbil, Kurdistan. We design and build web apps, AI integrations, and mobile products, from the first line of code to production.',
  },
  header: {
    nav: {
      home: 'Home',
      portfolio: 'Portfolio',
      products: 'Products',
      about: 'About',
      contact: 'Contact',
      careers: 'Careers',
    },
    toggleMenuAriaLabel: 'Toggle menu',
    email: 'info@datacode.app',
    socials: {
      instagramAriaLabel: 'Instagram',
      linkedinAriaLabel: 'LinkedIn',
      twitterAriaLabel: 'Twitter/X',
    },
    tagline: 'Datacode',
    languageSwitcher: {
      ariaLabel: 'Choose language',
      en: 'EN',
      ar: 'عربي',
      ckb: 'کوردی',
    },
  },
  footer: {
    eyebrow: 'Start a project',
    // Two-line pitch. Keep as two entries since it renders as two lines
    // ("Have something to build?" then a dimmer sub-line).
    pitchLine1: 'Have something to build?',
    pitchLine2: "Talk to the people who'll build it.",
    email: 'info@datacode.app',
    phone: '+964-751-449-1008',
    address: {
      line1: 'English Village Rd',
      line2: 'Erbil, Kurdistan Region, Iraq',
    },
    columns: {
      studio: 'Studio',
      services: 'Services',
      products: 'Products',
      contact: 'Contact',
    },
    studioLinks: {
      home: 'Home',
      portfolio: 'Portfolio',
      about: 'About',
      articles: 'Articles',
      careers: 'Careers',
    },
    serviceLinks: {
      webDesign: 'Web Design & Development',
      mobileApp: 'Mobile App Development',
      aiIntegration: 'AI Integration',
      uiUx: 'UI/UX Design',
      allServices: 'All services',
    },
    productLinks: {
      dilaCrm: 'DilaCRM',
      dilaErp: 'DilaERP',
      dilaMenu: 'dila.menu',
      allProducts: 'All products',
    },
    legalLinks: {
      support: 'Support',
      terms: 'Terms',
      privacy: 'Privacy',
    },
    copyright: 'Datacode',
    erbilLabel: 'Erbil',
    backToTop: 'Top',
  },
  hero: {
    // Rendered letter-by-letter, kept as a single word so the split('')
    // animation still works.
    title: 'DATACODE',
    subtitleMobile:
      'We design and build web apps, AI tools, and mobile products, from the first line of code to production. Built in Kurdistan, shipped worldwide.',
    subtitleDesktopLine1: 'We design and build web apps, AI tools, and mobile',
    subtitleDesktopLine2: 'products, from the first line of code to production.',
    subtitleDesktopLine3: 'Built in Kurdistan, shipped worldwide.',
    subtitleRevealLine1: 'No templates. No middlemen. No hand-off to a junior team.',
    subtitleRevealLine2: 'You work directly with the people writing the code.',
    subtitleRevealLine3: "That's the whole point.",
    scrollToExplore: 'Scroll to Explore',
    statusLine: {
      location: 'erbil hq',
      systemsLive: 'systems live',
    },
  },
  portfolio: {
    // Visually hidden heading above the featured-work gallery.
    sectionHeading: 'Featured work',
    loading: 'Loading projects...',
    empty: 'No projects available yet.',
    getInTouch: 'Get In Touch',
    imageUnavailable: 'Image unavailable',
    viewLabel: 'View',
  },
  statement: {
    row1: {
      webApps: 'WEB APPS',
      aiTools: 'AI TOOLS',
      mobile: 'MOBILE',
      platforms: 'PLATFORMS',
      production: 'PRODUCTION',
    },
    row2: {
      design: 'DESIGN',
      build: 'BUILD',
      ship: 'SHIP',
      scale: 'SCALE',
      support: 'SUPPORT',
    },
    mockupAlt: 'Multi App Interface',
  },
  glassStack: {
    // Visually hidden heading; the eyebrow below it is the visible label.
    sectionHeading: 'How we build',
    eyebrow: 'How we build',
    layers: {
      infrastructure: {
        title: 'Infrastructure & Security',
        desc: 'Deployment, monitoring, and security handled from day one, the layer everything else stands on.',
      },
      ai: {
        title: 'AI & Agents',
        desc: 'Intelligent features and automation wired into real products, assistants that ship, not demos.',
      },
      web: {
        title: 'Web Platforms',
        desc: 'Websites and platforms that load fast, rank on search, and hold up under real traffic.',
      },
      mobile: {
        title: 'Mobile Apps',
        desc: 'Polished apps for iPhone and Android, shipped to both stores by one team.',
      },
      design: {
        title: 'Design & Interaction',
        desc: 'Interfaces designed in-house, motion, brand, and UX decided before a line of code.',
      },
    },
  },
  skills: {
    // Visually hidden heading; the eyebrow below it is the visible label.
    sectionHeading: 'What we cover',
    eyebrow: 'What we cover',
    fallback: {
      webDesign: 'Web Design',
      mobileAppDevelopment: 'Mobile App Development',
      uiUxDesign: 'UI/UX Design',
      aiEngineering: 'AI Engineering',
      aiAgentsIntegrations: 'AI Agents and Integrations',
      techDueDiligence: 'Tech Due Diligence',
      fractionalCto: 'Fractional CTO and Team Building',
      mvpMobileWebsite: 'MVP Mobile/Website',
    },
  },
  contact: {
    eyebrow: 'Start a project',
    title: "LET'S TALK",
    subtitle:
      "Tell us what you want to build. You'll hear back from the people who would actually build it, not a sales desk.",
    email: 'info@datacode.app',
    phone: '+964-751-449-1008',
    labels: {
      email: 'Email',
      location: 'Location',
      phone: 'Phone',
    },
    location: {
      line1: 'Erbil, Kurdistan Region',
      line2: 'Iraq',
    },
    form: {
      nameLabel: 'Name',
      emailLabel: 'Email',
      messageLabel: 'Message',
      sending: 'Sending...',
      send: 'Send Message',
      success: "Thank you! Your message has been sent, we'll be in touch.",
      error: 'Something went wrong. Email us at info@datacode.app',
      validation: {
        nameMin: 'Name must be at least 2 characters',
        emailInvalid: 'Please enter a valid email',
        messageMin: 'Message must be at least 10 characters',
      },
    },
  },
  ourStudio: {
    eyebrow: 'Design & interaction',
    // Rendered letter-by-letter, kept as a single phrase so the split('')
    // animation still works.
    title: 'OUR STUDIO',
  },
  careers: {
    index: {
      eyebrow: 'Careers',
      heading: 'open roles',
      subtitle:
        "We're hiring locally in Erbil, Kurdistan, people who can work independently and own their pipeline, not just fill a seat.",
      empty: 'No open roles right now, check back soon.',
      metaTitle: 'Careers',
      metaDescription:
        'Open positions at Datacode: Sales Specialist and PR & Sales Specialist roles, hiring locally in Erbil, Kurdistan.',
    },
    job: {
      openings: 'openings',
      viewRole: 'View role',
      backToAll: 'All open roles',
      applyNow: 'Apply Now',
      applyForRole: 'Apply for this role',
      whatYouWillDo: "What you'll do",
      whatWereLookingFor: "What we're looking for",
      ogSuffix: 'Datacode Careers',
    },
  },
} as const;

/** The shape every locale dictionary must satisfy, with leaves widened to
 * `string` so translated locales can hold different text at the same keys. */
export type Dictionary = DeepWidenToString<typeof en>;

type DeepWidenToString<T> = T extends string
  ? string
  : { [K in keyof T]: DeepWidenToString<T[K]> };
